import {
  doc,
  getDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { EmployerLead } from '@/models/pilot-crm.model';

const PILOT_CRM_LEADS_COLLECTION = 'pilotCrmLeads';
const N8N_WEBHOOK_BASE_URL = import.meta.env.VITE_N8N_WEBHOOK_URL || '';

export type EnrichmentStatus = 'not_enriched' | 'pending' | 'enriched' | 'failed';

export interface EnrichmentMetadata {
  status: EnrichmentStatus;
  lastEnrichedAt?: Date;
  source?: string;
  confidence?: number;
  enrichedFields?: string[];
  error?: string;
}

export interface EnrichedData {
  estimatedEmployeeCount?: string;
  decisionMakers?: Array<{ name: string; title: string; email?: string }>;
  socialLinks?: { linkedin?: string; twitter?: string; facebook?: string };
  additionalEmails?: string[];
  businessDescription?: string;
  foundedYear?: number;
  annualRevenue?: string;
}

export class LeadEnrichmentService {
  static async getEnrichmentStatus(leadId: string): Promise<EnrichmentMetadata> {
    const docRef = doc(db, PILOT_CRM_LEADS_COLLECTION, leadId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Lead not found');
    }

    const data = docSnap.data();
    const enrichment = data.enrichment;

    if (!enrichment) {
      return { status: 'not_enriched' };
    }

    return {
      status: enrichment.status || 'not_enriched',
      lastEnrichedAt: enrichment.lastEnrichedAt?.toDate(),
      source: enrichment.source,
      confidence: enrichment.confidence,
      enrichedFields: enrichment.enrichedFields,
      error: enrichment.error,
    };
  }

  static async enrichLead(leadId: string, userId: string): Promise<void> {
    const docRef = doc(db, PILOT_CRM_LEADS_COLLECTION, leadId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Lead not found');
    }

    await updateDoc(docRef, {
      'enrichment.status': 'pending',
      'enrichment.requestedAt': Timestamp.now(),
      'enrichment.requestedBy': userId,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    });

    if (N8N_WEBHOOK_BASE_URL) {
      try {
        const response = await fetch(`${N8N_WEBHOOK_BASE_URL}/pilot-crm-lead-enrichment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            leadId,
            userId,
          }),
        });

        if (!response.ok) {
          throw new Error(`Enrichment webhook failed: ${response.status}`);
        }
      } catch (error) {
        await updateDoc(docRef, {
          'enrichment.status': 'failed',
          'enrichment.error': error instanceof Error ? error.message : 'Unknown error',
          updatedAt: Timestamp.now(),
          updatedBy: userId,
        });
        throw error;
      }
    }
  }

  static async updateEnrichmentData(
    leadId: string,
    enrichedData: EnrichedData,
    source: string,
    confidence: number,
    userId: string
  ): Promise<void> {
    const docRef = doc(db, PILOT_CRM_LEADS_COLLECTION, leadId);
    const now = Timestamp.now();

    const enrichedFields: string[] = [];
    const updates: Record<string, any> = {
      updatedAt: now,
      updatedBy: userId,
      'enrichment.status': 'enriched',
      'enrichment.lastEnrichedAt': now,
      'enrichment.source': source,
      'enrichment.confidence': confidence,
    };

    if (enrichedData.estimatedEmployeeCount) {
      updates.employeeCountRange = enrichedData.estimatedEmployeeCount;
      enrichedFields.push('employeeCountRange');
    }

    if (enrichedData.decisionMakers && enrichedData.decisionMakers.length > 0) {
      updates['enrichment.decisionMakers'] = enrichedData.decisionMakers;
      enrichedFields.push('decisionMakers');
    }

    if (enrichedData.socialLinks) {
      updates['enrichment.socialLinks'] = enrichedData.socialLinks;
      enrichedFields.push('socialLinks');
    }

    if (enrichedData.additionalEmails && enrichedData.additionalEmails.length > 0) {
      updates['enrichment.additionalEmails'] = enrichedData.additionalEmails;
      enrichedFields.push('additionalEmails');
    }

    if (enrichedData.businessDescription) {
      updates['enrichment.businessDescription'] = enrichedData.businessDescription;
      enrichedFields.push('businessDescription');
    }

    if (enrichedData.foundedYear) {
      updates['enrichment.foundedYear'] = enrichedData.foundedYear;
      enrichedFields.push('foundedYear');
    }

    if (enrichedData.annualRevenue) {
      updates['enrichment.annualRevenue'] = enrichedData.annualRevenue;
      enrichedFields.push('annualRevenue');
    }

    updates['enrichment.enrichedFields'] = enrichedFields;

    await updateDoc(docRef, updates);
  }

  static async getEnrichedData(leadId: string): Promise<EnrichedData | null> {
    const docRef = doc(db, PILOT_CRM_LEADS_COLLECTION, leadId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    const enrichment = data.enrichment;

    if (!enrichment || enrichment.status !== 'enriched') {
      return null;
    }

    return {
      estimatedEmployeeCount: data.employeeCountRange,
      decisionMakers: enrichment.decisionMakers,
      socialLinks: enrichment.socialLinks,
      additionalEmails: enrichment.additionalEmails,
      businessDescription: enrichment.businessDescription,
      foundedYear: enrichment.foundedYear,
      annualRevenue: enrichment.annualRevenue,
    };
  }

  static getConfidenceLabel(confidence: number): string {
    if (confidence >= 0.8) return 'High';
    if (confidence >= 0.5) return 'Medium';
    return 'Low';
  }

  static getConfidenceColor(confidence: number): string {
    if (confidence >= 0.8) return 'green';
    if (confidence >= 0.5) return 'yellow';
    return 'red';
  }
}
